import { normalizarEmpresa } from "./empresaContacto";
import { client } from "./supabase";

// Selección de "prueba social" para el mail del Radar Comercial — hasta 3 fotos RECIENTES de
// marcas top del mismo país del destinatario (regla 10.281 — nunca se cruza país), nunca del
// propio destinatario ni de una marca de su misma categoría (a un banco no se le muestra otro
// banco). Con menos de 3 marcas elegibles con foto, se devuelven las que haya (2, 1 o 0) — nunca
// se fuerza a 3 (decisión de Ignacio, 2026-09-27).
//
// Fuente (cambio 2026-09-27, decisión de Ignacio): `galeria_fotos` — la galería CURADA por el
// equipo como las mejores fotos para exhibir, no `reportes_fotograficos` (archivo completo sin
// curar). Solo marcas ya presentes en `empresas_vip` de ese país (Ignacio decide qué marcas suman
// a esa lista, ver docs/modulos/alertas-recurrencia-comercial.md). Estructuralmente no puede haber
// BTL en `galeria_fotos` (sin `contrato_id`/`periodo`, sin ningún `screen_type` de activación) —
// la regla "BTL fuera para siempre" se cumple sola, no depende de un filtro acá.
//
// País de una foto = mismo criterio ya establecido en `src/components/Galeria/index.js`
// (`applyFiltersHelper`/`countryOptions`): `clientePais || edificioPais` (el cliente gana si tiene
// país cargado, el edificio es fallback) — no es un criterio nuevo para el Radar (10.1).
//
// Etiqueta de cada foto = `clientes.producto` (la MARCA, ej. "Corona") del cliente específico
// ligado a esa foto — nunca `clientes.empresa` (el holding, ej. "ABINBEV"): un holding VIP puede
// tener varias marcas/productos, y la foto es de una marca puntual, no del holding entero.
//
// Excepción (2026-09-27, caso WOM): si `producto` es un rótulo GENÉRICO (B2B/B2C/CORPORATIVO/
// INSTITUCIONAL) o coincide con el nombre de la categoría del cliente (ej. KLASSIKCAR ·
// "AUTOMOTRIZ" = "AUTOMOTRIZ"), no identifica ninguna marca real — la etiqueta (y el nombre del
// archivo adjunto) usan `empresa` en su lugar (WOM, no "B2B").

// Ventana única (2026-09-27, decisión de Ignacio: 45→120 días, simplificada a una sola constante
// — con `empresas_vip` ampliada la cobertura ya alcanza sin necesidad del escalón 45→90 anterior).
const VENTANA_DIAS = 120;

const restarDias = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString();
};

const PRODUCTOS_GENERICOS = new Set(["B2B", "B2C", "CORPORATIVO", "INSTITUCIONAL"]);

// Marca real a mostrar/nombrar para un cliente — ver comentario de excepción arriba.
const resolverMarca = (cliente, nombreCategoriaPorId) => {
  const producto = (cliente.producto || "").trim();
  if (!producto) return cliente.empresa.trim();
  const productoUpper = producto.toUpperCase();
  const categoriaUpper = (nombreCategoriaPorId.get(cliente.categoria) || "").trim().toUpperCase();
  const esGenerico =
    PRODUCTOS_GENERICOS.has(productoUpper) || (categoriaUpper !== "" && productoUpper === categoriaUpper);
  return esGenerico ? cliente.empresa.trim() : producto;
};

async function cargarClientesGlobal() {
  const { data, error } = await client
    .from("clientes")
    .select("id, empresa, producto, categoria, pais")
    .eq("eliminada", false);
  if (error) throw error;
  return new Map((data || []).map((c) => [c.id, c]));
}

async function cargarEdificiosGlobal() {
  const { data, error } = await client.from("edificios").select("id, pais");
  if (error) throw error;
  return new Map((data || []).map((e) => [e.id, e]));
}

async function cargarCategoriasGlobal() {
  const { data, error } = await client.from("categorias").select("id, nombre");
  if (error) throw error;
  return new Map((data || []).map((c) => [c.id, c.nombre]));
}

// Marcas VIP elegibles del país (excluye al destinatario y a cualquier marca de su misma
// categoría) — devuelve un Set de empresa_norm.
async function marcasElegibles({ pais, categoriaDestinatario, empresaNormDestinatario, clientesPorId }) {
  const { data: vipsRaw, error: errVips } = await client.from("empresas_vip").select("empresa_norm").eq("pais", pais);
  if (errVips) throw errVips;
  const vipSet = new Set((vipsRaw || []).map((v) => v.empresa_norm).filter((e) => e !== empresaNormDestinatario));
  if (vipSet.size === 0 || categoriaDestinatario == null) return vipSet;

  const categoriasPorEmpresaNorm = new Map();
  for (const c of clientesPorId.values()) {
    if (!c.empresa || c.pais !== pais || c.categoria == null) continue;
    const en = normalizarEmpresa(c.empresa);
    if (!vipSet.has(en)) continue;
    if (!categoriasPorEmpresaNorm.has(en)) categoriasPorEmpresaNorm.set(en, new Set());
    categoriasPorEmpresaNorm.get(en).add(c.categoria);
  }
  const elegibles = new Set(vipSet);
  for (const [en, cats] of categoriasPorEmpresaNorm) {
    if (cats.has(categoriaDestinatario)) elegibles.delete(en);
  }
  return elegibles;
}

// Mejor foto (más reciente, válida) por marca dentro de una ventana de días.
async function mejorFotoPorMarca({ pais, dias, empresasElegibles, clientesPorId, edificiosPorId, categoriasPorId }) {
  const cutoff = restarDias(dias);
  const { data: fotos, error } = await client
    .from("galeria_fotos")
    .select("id, idcliente, idedificio, url, created_at")
    .not("idcliente", "is", null)
    .gte("created_at", cutoff)
    .order("created_at", { ascending: false });
  if (error) throw error;

  const mejorPorMarca = new Map(); // empresaNorm -> {empresaNorm, marca, url, fechaFoto, idedificio, idFoto}
  for (const f of fotos || []) {
    const c = clientesPorId.get(f.idcliente);
    if (!c || !c.empresa) continue;
    const paisFoto = c.pais || edificiosPorId.get(f.idedificio)?.pais || null;
    if (paisFoto !== pais) continue; // 10.281 — nunca cruzar país
    const en = normalizarEmpresa(c.empresa);
    if (!empresasElegibles.has(en) || mejorPorMarca.has(en)) continue; // ya tiene su más-reciente (orden DESC)
    mejorPorMarca.set(en, {
      empresaNorm: en,
      marca: resolverMarca(c, categoriasPorId),
      url: f.url,
      fechaFoto: f.created_at,
      idedificio: f.idedificio,
      idFoto: f.id,
    });
  }
  return mejorPorMarca;
}

// Dado el pool de candidatas (empresaNorm -> mejor foto), elige 3 marcas distintas priorizando
// edificios distintos: recorre en orden determinista (fecha DESC, empresaNorm ASC) y en una
// primera pasada toma solo edificios no vistos; si no alcanza a completar 3 así, una segunda
// pasada completa con lo que quede, permitiendo repetir edificio.
function elegirTres(mejorPorMarca) {
  const candidatos = [...mejorPorMarca.values()].sort((a, b) => {
    if (a.fechaFoto !== b.fechaFoto) return a.fechaFoto < b.fechaFoto ? 1 : -1;
    return a.empresaNorm < b.empresaNorm ? -1 : 1;
  });

  const elegidas = [];
  const edificiosUsados = new Set();
  for (const c of candidatos) {
    if (elegidas.length >= 3) break;
    if (!edificiosUsados.has(c.idedificio)) {
      elegidas.push(c);
      edificiosUsados.add(c.idedificio);
    }
  }
  if (elegidas.length < 3) {
    const empresasYaElegidas = new Set(elegidas.map((e) => e.empresaNorm));
    for (const c of candidatos) {
      if (elegidas.length >= 3) break;
      if (!empresasYaElegidas.has(c.empresaNorm)) {
        elegidas.push(c);
        empresasYaElegidas.add(c.empresaNorm);
      }
    }
  }
  return elegidas;
}

/**
 * Selecciona hasta 3 fotos recientes de galería de marcas VIP del mismo país del destinatario,
 * para la sección de "prueba social" del mail del Radar — nunca la empresa del destinatario ni
 * una de su misma categoría. Ventana única de 120 días (2026-09-27).
 *
 * @param {{pais:string, clienteIdDestinatario:(number|null), empresaDestinatario:string}} args
 *   `pais` en convención NOMBRE ("Chile"/"Perú", 10.53/10.281 — la misma de `clientes.pais`).
 *   `clienteIdDestinatario`: id de `clientes` de la venta de referencia de la alerta — se usa
 *   SOLO para resolver su categoría (excluir marcas de la misma categoría), nunca se modifica.
 * @returns {Promise<{
 *   fotos:Array<{empresaNorm:string,marca:string,url:string,fechaFoto:string,idedificio:number,idFoto:number}>
 * }>}
 */
export async function seleccionarFotosRadar({ pais, clienteIdDestinatario, empresaDestinatario }) {
  const empresaNormDestinatario = normalizarEmpresa(empresaDestinatario);

  let categoriaDestinatario = null;
  if (clienteIdDestinatario != null) {
    const { data: destCliente, error: errDest } = await client
      .from("clientes")
      .select("categoria")
      .eq("id", clienteIdDestinatario)
      .maybeSingle();
    if (errDest) throw errDest;
    categoriaDestinatario = destCliente?.categoria ?? null;
  }

  const clientesPorId = await cargarClientesGlobal();
  const empresasElegibles = await marcasElegibles({
    pais,
    categoriaDestinatario,
    empresaNormDestinatario,
    clientesPorId,
  });
  if (empresasElegibles.size === 0) return { fotos: [] };

  const [edificiosPorId, categoriasPorId] = await Promise.all([cargarEdificiosGlobal(), cargarCategoriasGlobal()]);
  const mejorPorMarca = await mejorFotoPorMarca({
    pais,
    dias: VENTANA_DIAS,
    empresasElegibles,
    clientesPorId,
    edificiosPorId,
    categoriasPorId,
  });

  return { fotos: elegirTres(mejorPorMarca) };
}
